import { useCallback, useEffect, useRef } from 'react'
import { buildChannelMessageLocation } from '@common/helpers/notificationRouting'
import { firstNameOf } from '@common/helpers/participantTitle'
import { router } from '../../appRouter'
import { shouldOpenMessageInBubble } from '../../helpers/messageNotifications'
import { usePermissions } from '../../helpers/permissionContext'
import { useIsMobile } from '../../hooks/useIsMobile'
import MemberAvatar from '../../pages/Messages/components/MemberAvatar'
import { IMessageToast } from '../../redux/ui/actions'
import Icon from '../Common/Icon'
import IconButton from '../Common/IconButton'
import Text from '../Common/Text'
import styles from './MessageToasts.module.scss'
import { PropsFromRedux } from './index'

export type IProps = PropsFromRedux

/** Long enough to read two lines, short enough not to sit on the page. */
const AUTO_DISMISS_MS = 7_000

interface IToastProps {
  toast: IMessageToast
  onOpen: (toast: IMessageToast) => void
  onDismiss: (messageId: string) => void
}

const MessageToast = ({ toast, onOpen, onDismiss }: IToastProps) => {
  // Held in a ref so the hover-pause below can cancel it without the timer being a
  // dependency that re-arms itself on every render.
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const startTimer = useCallback(() => {
    timerRef.current = setTimeout(() => onDismiss(toast.messageId), AUTO_DISMISS_MS)
  }, [onDismiss, toast.messageId])

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [])

  useEffect(() => {
    startTimer()
    return clearTimer
  }, [startTimer, clearTimer])

  const handleOpen = useCallback(() => onOpen(toast), [onOpen, toast])

  // First name on the label, matching every other sender label in Messages. The avatar
  // keeps the full name so its two initials still tell two Bens apart.
  const senderLabel = toast.senderShortName ?? firstNameOf(toast.senderName)

  const handleDismiss = useCallback(() => onDismiss(toast.messageId), [onDismiss, toast.messageId])

  return (
    // Two sibling buttons rather than a clickable card wrapping a close button: a
    // `role="button"` container would nest interactive controls, and give the card
    // keyboard semantics it then has to reimplement. Real buttons come with Enter
    // and Space already, and the card itself stays a plain box.
    <div
      className={styles.toast}
      // Pause the countdown while the pointer is on it — a toast should not vanish
      // out from under someone who is reading it or reaching for the close button.
      onMouseEnter={clearTimer}
      onMouseLeave={startTimer}
    >
      <button type="button" className={styles.open} onClick={handleOpen}>
        <MemberAvatar
          userId={toast.senderUserId}
          name={toast.senderName}
          className={styles.avatar}
        />
        <div className={styles.body}>
          <div className={styles.header}>
            <span className={styles.sender}>{senderLabel}</span>
            <span className={styles.channel}>{toast.channelLabel}</span>
          </div>
          <Text tone="secondary" casing="none" className={styles.preview}>
            {toast.preview}
          </Text>
        </div>
      </button>
      <IconButton
        title="Dismiss"
        box="sm"
        size="small"
        className={styles.close}
        onClick={handleDismiss}
        aria-label={`Dismiss message from ${senderLabel}`}
      >
        <Icon name="x" size="sm" />
      </IconButton>
    </div>
  )
}

/**
 * In-app toasts for messages that arrive while you're using Atlas but aren't in the
 * conversation they landed in.
 *
 * Separate from the global Snackbar on purpose: that is one slot of feedback about
 * something you just did, and it replaces itself. These are unsolicited, arrive in
 * bursts, and have to say *who* — so they stack, carry an avatar, and open the
 * conversation when clicked.
 *
 * Mounted once at the app root so a message arriving anywhere in the portal surfaces
 * the same way.
 */
const MessageToasts = ({ toasts, dismissMessageToast, requestOpenMessagesChannel }: IProps) => {
  const { isInternalUser } = usePermissions()
  // One toast stack, not a list of rows, so subscribing here is cheap. There is no
  // bubble below `md` — only the full Messages screen — so a tap has to navigate.
  const isMobile = useIsMobile()

  const handleOpen = useCallback(
    (toast: IMessageToast) => {
      dismissMessageToast(toast.messageId)
      // Both hosts get the message, so a tap lands where the toast pointed rather
      // than at the conversation's default view.
      // router.state rather than useLocation/useNavigate: this renders outside
      // RouterProvider, so there is no router context to hook into.
      if (shouldOpenMessageInBubble(router.state.location.pathname, isInternalUser, isMobile)) {
        requestOpenMessagesChannel(toast.channelId, {
          messageId: toast.messageId,
        })
        return
      }
      void router.navigate(
        buildChannelMessageLocation({
          channelId: toast.channelId,
          messageId: toast.messageId,
        }),
      )
    },
    [dismissMessageToast, isInternalUser, isMobile, requestOpenMessagesChannel],
  )

  if (!toasts.length) return null

  return (
    <div className={styles.stack}>
      {toasts.map((toast) => (
        <MessageToast
          key={toast.messageId}
          toast={toast}
          onOpen={handleOpen}
          onDismiss={dismissMessageToast}
        />
      ))}
    </div>
  )
}

export default MessageToasts
